import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { Copy } from '@yandex-360/iconostasis/dist/icons/Copy';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { ListItem } from '@yandex-360/orb-components/dist/components/ListItem';
import { Select, SelectProps } from '@yandex-360/orb-components/dist/components/Select2';
import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';

import { NOTIFICATIONS_MAP } from '../../../../../../shared/features/notifications/ps-notifications';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { useActions } from '../../../../../../shared/hooks';
import { NotificationId } from '../../../../../call/types';
import { Artifact, RecipientFetchingStatus } from '../../../fetching/types';
import { getArtifactRecipientsSelectItems } from '../../helpers/get-recepients-select-items';
import { copyToCB } from '../../slice';
import styles from './styles.css';

type RecipientsSelectProps = Omit<SelectProps, 'items'> & {
    onFetchMoreRecipientsPress: () => void;
    artifact: Artifact;
    onCopyEmailPress?: () => void;
};

export const RecipientsSelect: FunctionComponent<RecipientsSelectProps> = ({
    artifact,
    onFetchMoreRecipientsPress,
    onCopyEmailPress,
    ...rest
}) => {
    const actions = useActions({
        copyToCB,
    });
    const extendedOnCopyEmailPress = useCallback(
        (email: string) => {
            onCopyEmailPress?.();
            actions.copyToCB({ value: email, notificationItem: NOTIFICATIONS_MAP[NotificationId.EMAIL_COPIED]() });
        },
        [actions, onCopyEmailPress]
    );
    const selectItems = useMemo(() => {
        const items = getArtifactRecipientsSelectItems(artifact.recipients);
        const moreRecipientsCount = (artifact.recipientsTotal || 0) - artifact.recipients.length;
        if (artifact.recipientFetchingStatus !== RecipientFetchingStatus.FETCHED && moreRecipientsCount > 0) {
            items.push({
                itemKey: 'fetch-button',
                label: i18n('conferences-history', 'show-more-recipients', { count: moreRecipientsCount }),
            });
        }
        return items;
    }, [artifact.recipientFetchingStatus, artifact.recipients, artifact.recipientsTotal]);
    if (artifact.recipients.length === 0) {
        return <></>;
    }
    return (
        <Select
            popupWidth="var(--conferences-history-drawer-recipients-select-width)"
            items={selectItems}
            popupProps={{ placement: 'top right' }}
            {...rest}
        >
            {({ itemKey, label }) => (
                <>
                    {itemKey !== 'fetch-button' && (
                        <ListItem
                            interaction={false}
                            label={label}
                            description={itemKey}
                            endSlot={
                                itemKey ? (
                                    <TooltipStateful
                                        content={i18n('conferences-history', 'copy-email')}
                                        trigger="hover"
                                        className={styles.tooltip}
                                    >
                                        <Button
                                            view="contrast-transparent"
                                            icon={<Copy variant="outline" size="sm" />}
                                            onPress={() => extendedOnCopyEmailPress(itemKey as string)}
                                        />
                                    </TooltipStateful>
                                ) : undefined
                            }
                        />
                    )}
                    {itemKey === 'fetch-button' && (
                        <Button
                            isPending={artifact.recipientFetchingStatus === RecipientFetchingStatus.FETCHING}
                            onPress={onFetchMoreRecipientsPress}
                            className={styles.fetchMoreRecipients}
                        >
                            {label}
                        </Button>
                    )}
                </>
            )}
        </Select>
    );
};
